import { User, UserRole } from '../types';

/**
 * Catalogue centralisé des permissions atomiques.
 * Source de vérité unique pour : l'UI de gestion des profils, les checks UI
 * (boutons, dropdowns) et — à terme — les checks backend.
 *
 * Convention : `<domaine>.<verbe>` en camelCase.
 *
 * Phase actuelle (option C) : les 4 rôles de base gardent leurs droits structurels
 * hardcodés (visibilité leads, accès paramètres). Les permissions ci-dessous sont
 * des AJUSTEMENTS opérationnels qui s'appliquent en plus du rôle de base.
 *
 * Évolution future (option B) : on pourra remplacer progressivement les checks
 * hardcodés par `hasPermission()` sans toucher au catalogue.
 */
export type PermissionKey =
  // Prospects — visibilité
  | 'leads.viewAll'
  // Prospects — édition
  | 'leads.create'
  | 'leads.modify'
  | 'leads.exportCsv'
  | 'leads.importCsv'
  | 'leads.delete'
  | 'leads.bulkAssign'
  | 'leads.bulkUpdate'
  // Communication
  | 'comments.create'
  | 'comments.delete'
  | 'emails.send'
  | 'emails.manageTemplates'
  // Tâches
  | 'tasks.createForOthers'
  // Opportunités
  | 'opportunities.create'
  | 'opportunities.export'
  // Dashboard
  | 'dashboard.customize'
  | 'dashboard.viewSwitch';

export interface PermissionDef {
  key: PermissionKey;
  label: string;
  description: string;
  group: 'leads' | 'communication' | 'tasks' | 'opportunities' | 'dashboard';
  /** Valeur par défaut quand l'utilisateur n'a pas de profil custom (préserve l'UX historique). */
  defaultOn: boolean;
}

export const PERMISSION_CATALOG: ReadonlyArray<PermissionDef> = [
  // Leads — visibilité (étend le rôle de base)
  { key: 'leads.viewAll',       label: 'Voir tous les prospects de la compagnie', description: 'Étend la visibilité d\'un Utilisateur ou Manager à l\'ensemble des prospects de la compagnie (comme un Chef d\'équipe). Couplé à « Modifier un prospect » désactivé = lecture seule globale.', group: 'leads', defaultOn: false },
  // Leads — édition
  { key: 'leads.create',        label: 'Créer un prospect',                       description: 'Bouton « + Ajouter » et création depuis la fiche.',               group: 'leads',         defaultOn: true  },
  { key: 'leads.modify',        label: 'Modifier un prospect',                    description: 'Édition des champs de la fiche prospect, changement de statut, réassignation, etc. Désactivé = mode lecture seule.', group: 'leads', defaultOn: true },
  { key: 'leads.exportCsv',     label: 'Exporter les prospects en CSV',           description: 'Télécharger un fichier CSV de la liste de prospects.',           group: 'leads',         defaultOn: false },
  { key: 'leads.importCsv',     label: 'Importer des prospects',                  description: 'Upload CSV/XLSX avec mapping de colonnes.',                       group: 'leads',         defaultOn: false },
  { key: 'leads.delete',        label: 'Supprimer un prospect',                   description: 'Bouton de suppression dans la fiche prospect.',                   group: 'leads',         defaultOn: true  },
  { key: 'leads.bulkAssign',    label: 'Réassigner en masse',                     description: 'Action en masse "Assigner à" depuis la liste.',                   group: 'leads',         defaultOn: true  },
  { key: 'leads.bulkUpdate',    label: 'Modifier le statut en masse',             description: 'Action en masse "Changer statut" depuis la liste.',               group: 'leads',         defaultOn: true  },
  // Communication
  { key: 'comments.create',     label: 'Ajouter un commentaire',                  description: 'Poster un commentaire dans la timeline d\'une fiche prospect.',   group: 'communication', defaultOn: true  },
  { key: 'comments.delete',     label: 'Supprimer ses propres commentaires',      description: 'Pour un utilisateur : peut supprimer les commentaires qu\'il a postés. Sans effet sur Manager (équipe) / Chef d\'équipe / Admin (tout).', group: 'communication', defaultOn: false },
  { key: 'emails.send',         label: 'Envoyer des emails',                      description: 'Onglet email dans la fiche prospect (envoi SMTP).',               group: 'communication', defaultOn: true  },
  { key: 'emails.manageTemplates', label: "Gérer les modèles d'email",            description: 'Accès à l\'onglet Paramètres → Templates d\'email.',              group: 'communication', defaultOn: true  },
  // Tâches
  { key: 'tasks.createForOthers', label: 'Créer des tâches pour d\'autres',       description: 'Choisir un autre commercial comme destinataire d\'une tâche.',    group: 'tasks',         defaultOn: true  },
  // Opportunités
  { key: 'opportunities.create',  label: 'Créer des opportunités',                description: 'Bouton "Créer un devis" depuis la fiche prospect.',               group: 'opportunities', defaultOn: true  },
  { key: 'opportunities.export',  label: 'Exporter une opportunité en PDF',        description: 'Action "Télécharger PDF" sur une opportunité.',                  group: 'opportunities', defaultOn: true  },
  // Dashboard
  { key: 'dashboard.customize',   label: 'Personnaliser le dashboard',            description: 'Drag & drop des widgets, masquer/afficher.',                       group: 'dashboard',     defaultOn: false },
  { key: 'dashboard.viewSwitch',  label: 'Changer de vue (liste/cartes/kanban)',  description: 'Boutons de changement d\'affichage de la liste prospects.',       group: 'dashboard',     defaultOn: true  },
];

export const PERMISSION_GROUP_LABEL: Record<PermissionDef['group'], string> = {
  leads:         'Prospects',
  communication: 'Communication',
  tasks:         'Tâches',
  opportunities: 'Opportunités',
  dashboard:     'Tableau de bord',
};

/** Type d'un profil de rôle custom tel que stocké côté front. */
export interface CustomRole {
  id: string;
  name: string;
  description?: string;
  /** Map clé permission → autorisée. Absent = défaut catalogue. */
  permissions: Partial<Record<PermissionKey, boolean>>;
  /** Modèle de fiche prospect par défaut pour ce profil (override possible par user). */
  leadSheetTemplateId?: string | null;
}

/**
 * Résout une permission pour un utilisateur donné.
 *
 * Ordre de résolution (le premier qui répond gagne) :
 *  1. Rôles "structurels" : SUPER_ADMIN / COMPANY_ADMIN / TEAM_MANAGER ont
 *     TOUTES les permissions opérationnelles par défaut.
 *  2. Si l'utilisateur a un profil custom, on lit la valeur dans
 *     `customRole.permissions[key]` (true/false). Si absent → étape 3.
 *  3. Valeur `defaultOn` du catalogue.
 *
 * Ce helper est l'API stable pour évoluer vers du RBAC complet (option B) :
 * il suffira de retirer l'étape 1 quand le catalogue couvrira tout.
 */
export function hasPermission(
  user: Pick<User, 'role'> | undefined | null,
  key: PermissionKey,
  customRole?: CustomRole | null
): boolean {
  if (!user) return false;

  // Garde-fou structurel : les rôles d'administration commerciale gardent tout.
  if (
    user.role === UserRole.SUPER_ADMIN ||
    user.role === UserRole.COMPANY_ADMIN ||
    user.role === UserRole.TEAM_MANAGER
  ) {
    return true;
  }

  // Profil custom : surcharge explicite si la clé est définie.
  if (customRole && key in customRole.permissions) {
    const v = customRole.permissions[key];
    if (typeof v === 'boolean') return v;
  }

  // Sinon : valeur par défaut du catalogue (conserve l'UX historique).
  const def = PERMISSION_CATALOG.find(p => p.key === key);
  return def ? def.defaultOn : false;
}
